/**
 * Constants shared across the application
 *
 * Photo IQ formula (canonical definition -- keep in sync across all platforms):
 *   Photo IQ = round(average of composite_score for all photos with status "completed")
 *   Returns null/nil when there are no completed photos with scores.
 *   Implementations: web MeTab.tsx, iOS ProfileView.swift, iOS BattleView.swift
 */
// Supported AI models
export const SUPPORTED_MODELS = ['gpt-4o', 'gemini-flash', 'claude-haiku'];
// Score weights for composite calculation
// Can be adjusted based on A/B testing
export const MODEL_WEIGHTS = {
    'gpt-4o': 0.4,
    'gemini-flash': 0.3,
    'claude-haiku': 0.3,
};
// Default weights for unknown models
export const DEFAULT_MODEL_WEIGHT = 0.33;
// File upload constraints
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
export const ALLOWED_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
// Rate limiting
export const RATE_LIMITS = {
    uploadsPerMinute: 5,
    uploadsPerHour: 50,
    uploadsPerDay: 100,
    concurrentUploads: 3,
};
// Credit packages
export const CREDIT_PACKAGES = [
    { id: 'starter', credits: 5, price: 500, label: 'Starter' }, // $5.00
    { id: 'standard', credits: 10, price: 1000, label: 'Standard' }, // $10.00
    { id: 'pro', credits: 25, price: 2000, label: 'Pro', popular: true }, // $20.00 (20% discount)
    { id: 'custom', credits: 1, price: 100, label: 'Custom' }, // $1.00 per credit
];
// Free credits on signup
export const FREE_CREDITS_ON_SIGNUP = 3;
// Low credit warning threshold
export const LOW_CREDIT_THRESHOLD = 2;
// Photo retention period (days)
export const PHOTO_RETENTION_DAYS = 30;
// Polling interval for results page (ms)
export const POLLING_INTERVAL_MS = 2000;
// Analysis timeout (ms) — 90s; some LLM providers are slow; matches production web behavior
export const ANALYSIS_TIMEOUT_MS = 90000;
// Context-specific attributes
export const CONTEXT_ATTRIBUTES = {
    dating: ['attractiveness', 'trustworthiness', 'likability'],
    business: ['competence', 'influence', 'likability'],
    social: ['fun', 'confidence', 'authenticity'],
};
// Context options for UI
export const CONTEXT_OPTIONS = [
    { value: 'dating', label: 'Dating', description: 'Photos for dating apps' },
    { value: 'business', label: 'LinkedIn', description: 'LinkedIn and professional headshots' },
    { value: 'social', label: 'Social', description: 'Social media photos' },
];
// Batch photo shootout pricing
export const BATCH_PRICING = {
    small: { minPhotos: 2, maxPhotos: 5, credits: 3 },
    medium: { minPhotos: 6, maxPhotos: 10, credits: 5 },
    large: { minPhotos: 11, maxPhotos: 20, credits: 8 },
    xl: { minPhotos: 21, maxPhotos: 50, credits: 12 },
};
export const BATCH_MIN_PHOTOS = 2;
export const BATCH_MAX_PHOTOS = 50;
// Aliases for compatibility
export const CREDIT_PACKS = CREDIT_PACKAGES;
// Rating contexts array
export const RATING_CONTEXTS = ['dating', 'business', 'social'];
// JWT configuration
export const JWT_CONFIG = {
    accessTokenExpiry: '1h',
    refreshTokenExpiry: '30d',
};
// API endpoints
export const API_ENDPOINTS = {
    upload: '/upload',
    rate: '/rate',
    ratings: '/ratings',
    credits: '/credits',
    user: '/user',
};
